import {
  Chip,
  FormControl,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { Employee, EmployeeRole } from "employee/models";
import React, { useEffect, useState, FC } from "react";
import employeeService from "services/employeeService";
import adminService from "services/adminService";
import { RoleListItem } from "../../admin/models";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Can from "common/Can";
import { toast } from "react-toastify";
export interface EmployeeRolesProps {
  employee: Employee;
}

const EmployeeRoles: FC<EmployeeRolesProps> = (props) => {
  const [allRoles, setAllRoles] = useState<RoleListItem[]>([]);
  const [availableRoles, setAvailableRoles] = useState<RoleListItem[]>([]);
  const [roles, setRoles] = useState<EmployeeRole[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const addUserRole = async () => {
    setLoading(true);
    try {
      await employeeService.addEmployeeRole(+props.employee.id, selectedRoleId);
      const result = await employeeService.getEmployeeRoles(props.employee.id);
      setRoles(result);
      setSelectedRoleId(null);
    } finally {
      setLoading(false);
    }
  };

  const addAllRoles = async () => {
    // Recompute at click time; backend also dedupes, but skip the round-trip if nothing to do.
    const existingIds = new Set(roles.map((r) => r.roleId));
    const toAddIds = allRoles.filter((r) => !existingIds.has(r.roleId)).map((r) => r.roleId);
    if (toAddIds.length === 0) {
      toast.info("Employee already has every role.");
      return;
    }
    setLoading(true);
    try {
      await employeeService.addEmployeeRolesBulk(+props.employee.id, toAddIds);
      const refreshed = await employeeService.getEmployeeRoles(props.employee.id);
      setRoles(refreshed);
      toast.success(`Added ${toAddIds.length} role${toAddIds.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Error adding all roles");
    } finally {
      setLoading(false);
    }
  };

  const deleteEmployeeRole = async (roleId: number) => {
    setLoading(true);
    try {
      await employeeService.deleteEmployeeRole(props.employee.id, roleId);
      const result = await employeeService.getEmployeeRoles(props.employee.id);
      setRoles(result);
    } finally {
      setLoading(false);
    }
  };

  const removeAllRoles = async () => {
    if (roles.length === 0) return;
    if (!window.confirm(`Remove all ${roles.length} roles from this employee?`)) return;
    setLoading(true);
    try {
      await employeeService.removeEmployeeRolesBulk(
        props.employee.id,
        roles.map((r) => r.roleId!)
      );
      const refreshed = await employeeService.getEmployeeRoles(props.employee.id);
      setRoles(refreshed);
      toast.success("All roles removed");
    } catch (e) {
      toast.error("Error removing all roles");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    adminService.getRoles().then((results) => {
      setAllRoles(results);
      setAvailableRoles(results);
      employeeService.getEmployeeRoles(props.employee.id).then((result) => {
        setRoles(result);
      });
    });
  }, []);

  useEffect(() => {
    setAvailableRoles(allRoles.filter((f) => !roles.some((r) => f.roleId == r.roleId)));
  }, [roles, allRoles]);

  return (
    <>
      <Grid item xs={12} mt={2}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h5">Employee Roles</MDTypography>
          </MDBox>
          <MDBox component="form" pb={3} px={3}>
          <Grid container spacing="2">
          <Can perform={'AssignEmployeeRoles'} yes={()=>(
            <Grid item xs={12} md={8}>
              <FormControl>
                <InputLabel id="roles-label">Roles</InputLabel>
                <Select
                  id="roles"
                  name="roles"
                  sx={{ minWidth: 210 }}
                  label="Roles"
                  labelId="roles-label"
                  value={selectedRoleId ?? ""}
                  onChange={(e) => setSelectedRoleId(+e.target.value!)}
                 
                >
                  {availableRoles &&
                    availableRoles.map((c, index) => (
                      <MenuItem key={index} value={c.roleId}>
                        {c.roleName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              
              <MDButton
                sx={{ ml: 1, height: 46 }}
                variant="contained"
                color="dark"
                onClick={addUserRole}
                disabled={loading}
              >
                Add
              </MDButton>
              <Tooltip
                title={
                  availableRoles.length === 0
                    ? "User has all roles"
                    : "Assign every remaining role to this employee"
                }
              >
                <span style={{ display: "inline-flex" }}>
                  <MDButton
                    sx={{ ml: 1, height: 46 }}
                    variant="outlined"
                    color="dark"
                    onClick={addAllRoles}
                    disabled={loading || availableRoles.length === 0}
                  >
                    Add All
                  </MDButton>
                </span>
              </Tooltip>
              {roles.length > 0 && (
                <MDButton
                  sx={{ ml: 1, height: 46 }}
                  variant="outlined"
                  color="error"
                  onClick={removeAllRoles}
                  disabled={loading}
                  title="Remove every role from this employee"
                >
                  Remove All
                </MDButton>
              )}
              
            </Grid>
            )}/>
            <Grid item sx={{ mt: 2 }} xs={12} md={8}>
              <List sx={{ display: "flex", flexDirection: "row" }}>
                {loading ? (
                  Array.from({ length: Math.max(roles.length || 3, 3) }).map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      width={100}
                      height={32}
                      sx={{ mr: 2, borderRadius: 4 }}
                    />
                  ))
                ) : roles && roles.length > 0 ? (
                  roles.map((r, i) => {
                    return (
                      <Can key={i} perform={'AssignEmployeeRoles'} yes={()=>(
                      <Chip key={r.roleId} sx={{ mr: 2 }} label={r.roleName} onDelete={() => deleteEmployeeRole(r.roleId!)} />
                      )} no={()=>(
                        <Chip key={r.roleId} sx={{ mr: 2 }} label={r.roleName} />
                      )}/>
                    );
                  })
                ) : (
                  <ListItem key={1}>No roles assigned</ListItem>
                )}
              </List>
            </Grid>
          </Grid>
          </MDBox>
        </Card>
      </Grid>
    </>
  );
};

export default EmployeeRoles;
